import { useEffect, useMemo, useState } from "react";
import { db } from "./firebase";
import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDocs,
  runTransaction,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import {
  calculateRecordFee,
  canonicalTeamName,
  isSalariedTeam,
  getBangkokDateParts,
} from "./utils/feeRules";
import { TEAM_MEMBERS } from "./utils/teamMembers";
import { useAuth } from "./AuthContext";
import useTeamDirectory, {
  getTeamRealName,
  formatTeamWithRealName,
} from "./hooks/useTeamDirectory";
import "./PaymentStatus.css";
import { writeAuditLog } from "./utils/auditLog";
import {
  EXTRA_WHT_METHOD,
  EXTRA_WHT_RATE,
  buildTeamPayout,
  calcCompanyTopUp,
  calcSalarySupport,
  calcSocialSecurity,
  getSalaryFloor,
  grossUpExtraIncome,
  extraAmounts,
  findAlreadySettled,
  getSalaryPayday,
  isOutstanding,
  itemCreatedAt,
  roundMoney,
  toDate,
  toTS,
} from "./utils/payoutCalc";

const OWNER_TEAM = "ยย";
const formatDate = (value) => {
  const date = toDate(value);
  if (!date) return "—";
  return date.toLocaleDateString("th-TH", {
    timeZone: "Asia/Bangkok",
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
};

const formatDateTime = (value) => {
  const date = toDate(value);
  if (!date) return "—";
  return date.toLocaleString("th-TH", {
    timeZone: "Asia/Bangkok",
    day: "2-digit",
    month: "short",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatPayday = (date) => {
  if (!date) return "วันที่ 26";
  return date.toLocaleDateString("th-TH", {
    timeZone: "Asia/Bangkok",
    day: "numeric",
    month: "short",
  });
};

const isSameBangkokDay = (left, right) => {
  if (!left || !right) return false;
  const a = getBangkokDateParts(left);
  const b = getBangkokDateParts(right);
  return a.year === b.year && a.month === b.month && a.day === b.day;
};

const todayBangkokInput = () => {
  const { year, month, day } = getBangkokDateParts(new Date());
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

const dateInputToBangkokDate = (value) => {
  if (!value) return new Date();
  return new Date(`${value}T12:00:00+07:00`);
};

export default function PaymentStatus({ showNotification }) {
  const { currentUser } = useAuth();
  const audit = (payload) => writeAuditLog({ user: currentUser, ...payload }).catch((error) => console.error("audit log failed", error));
  const [records, setRecords] = useState([]);
  const [extraIncomes, setExtraIncomes] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUnpaidOnly, setShowUnpaidOnly] = useState(true);
  const [teamQuery, setTeamQuery] = useState("");
  const [sortBy, setSortBy] = useState("amountDesc");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [detailTeam, setDetailTeam] = useState(null);
  const [confirmTeam, setConfirmTeam] = useState(null);
  const [settlementTeam, setSettlementTeam] = useState(null);
  const [savingTeam, setSavingTeam] = useState("");
  const [extraModalOpen, setExtraModalOpen] = useState(false);
  const [editingExtra, setEditingExtra] = useState(null);
  const [savingExtra, setSavingExtra] = useState(false);
  const [ownerModalOpen, setOwnerModalOpen] = useState(false);
  const [ownerEditRecord, setOwnerEditRecord] = useState(null);
  const [ownerFeeInput, setOwnerFeeInput] = useState("");
  const [ownerQtyInput, setOwnerQtyInput] = useState("");
  const [ownerDeleteConfirm, setOwnerDeleteConfirm] = useState(false);
  const [savingOwnerFee, setSavingOwnerFee] = useState(false);
  const [editingSettlement, setEditingSettlement] = useState(null);
  const [salaryDraft, setSalaryDraft] = useState(null);
  const [savingSettlement, setSavingSettlement] = useState(false);
  const { realNameByTeam } = useTeamDirectory();

  const [extraForm, setExtraForm] = useState({
    teamName: "",
    category: "ค่าคอม",
    description: "",
    amount: "",
    date: todayBangkokInput(),
    paymentStatus: "unpaid",
  });

  const [settlementForm, setSettlementForm] = useState({
    payrollWht: "0",
    socialSecurity: "0",
    actualTransfer: "",
    note: "",
  });

  const [settlementEditForm, setSettlementEditForm] = useState({
    payrollWht: "0",
    socialSecurity: "0",
    actualTransfer: "",
    note: "",
  });

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        const [recordSnapshot, extraSnapshot, settlementSnapshot] = await Promise.all([
          getDocs(collectionGroup(db, "records")),
          getDocs(collection(db, "team-extra-income")),
          getDocs(collection(db, "team-payment-settlements")),
        ]);

        if (!mounted) return;

        setRecords(
          recordSnapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
            ref: docSnap.ref,
          }))
        );
        setExtraIncomes(
          extraSnapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
            ref: docSnap.ref,
          }))
        );
        setSettlements(
          settlementSnapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
            ref: docSnap.ref,
          }))
        );
      } catch (error) {
        console.error("Failed to load payment data", error);
        showNotification?.("โหลดข้อมูลโอนค่ากดไม่สำเร็จ");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();
    return () => {
      mounted = false;
    };
  }, [showNotification]);

  const fmt = (number, digits = 0) =>
    Number(number || 0).toLocaleString("th-TH", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });

  const money = (number) => `${fmt(number, 2)} ฿`;

  const inDateRange = (createdAt) => {
    if (!dateFrom && !dateTo) return true;
    const date = toDate(createdAt);
    if (!date) return false;

    const start = dateFrom ? new Date(`${dateFrom}T00:00:00+07:00`) : null;
    const end = dateTo ? new Date(`${dateTo}T23:59:59.999+07:00`) : null;
    if (start && date < start) return false;
    if (end && date > end) return false;
    return true;
  };

  const ownerRecords = useMemo(
    () =>
      records
        .filter((record) => canonicalTeamName(record.teamName) === OWNER_TEAM)
        .filter((record) => inDateRange(record.createdAt))
        .sort((a, b) => toTS(b.createdAt) - toTS(a.createdAt)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [records, dateFrom, dateTo]
  );

  const ownerTotal = useMemo(
    () => ownerRecords.reduce((sum, record) => sum + calculateRecordFee(record).baseFee, 0),
    [ownerRecords]
  );

  const visibleRecords = useMemo(
    () =>
      records
        .filter((record) => canonicalTeamName(record.teamName) !== OWNER_TEAM)
        .filter((record) => inDateRange(record.createdAt))
        .filter((record) => (showUnpaidOnly ? isOutstanding(record) : true)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [records, showUnpaidOnly, dateFrom, dateTo]
  );

  const visibleExtras = useMemo(
    () =>
      extraIncomes
        .filter((item) => canonicalTeamName(item.teamName) !== OWNER_TEAM)
        .filter((item) => inDateRange(itemCreatedAt(item)))
        .filter((item) => (showUnpaidOnly ? isOutstanding(item) : true)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [extraIncomes, showUnpaidOnly, dateFrom, dateTo]
  );

  const groups = useMemo(() => {
    const map = {};

    const ensureTeam = (teamName) => {
      const team = canonicalTeamName(teamName) || "ไม่ระบุ";
      if (!map[team]) map[team] = { press: [], extras: [] };
      return map[team];
    };

    visibleRecords.forEach((record) => ensureTeam(record.teamName).press.push(record));
    visibleExtras.forEach((item) => ensureTeam(item.teamName).extras.push(item));

    let result = Object.entries(map).map(([team, bucket]) => {
      const list = bucket.press;
      const extraList = bucket.extras;
      const calculated = list.map((record) => ({
        record,
        fee: calculateRecordFee(record),
      }));
      const calculatedExtras = extraList.map((item) => ({
        item,
        amounts: extraAmounts(item),
      }));

      const pressNet = calculated.reduce((sum, item) => sum + item.fee.net, 0);
      const payable = pressNet + calculatedExtras.reduce((sum, item) => sum + item.amounts.net, 0);
      const baseFee = calculated.reduce((sum, item) => sum + item.fee.baseFee, 0);
      const commission = calculated.reduce((sum, item) => sum + item.fee.deducted, 0);
      const wht = calculated.reduce((sum, item) => sum + item.fee.tax, 0);
      const extraGross = calculatedExtras.reduce((sum, item) => sum + item.amounts.gross, 0);
      const extraWht = calculatedExtras.reduce((sum, item) => sum + item.amounts.wht, 0);
      const extraNet = calculatedExtras.reduce((sum, item) => sum + item.amounts.net, 0);
      const quantity = list.reduce((sum, record) => sum + Number(record.quantity || 0), 0);

      // ทุกยอด "ค้างโอน / รอบนี้ / เหลือหลังรอบนี้" มาจากฟังก์ชันเดียว (utils/payoutCalc)
      // เพื่อให้การ์ดทีม หน้ารายละเอียด และหน้ารอบโอนใช้รายการชุดเดียวกันเสมอ
      // พนักงานเงินเดือน: คิดรอบโอนจากข้อมูลทั้งหมดของทีมเสมอ (ไม่ขึ้นกับตัวกรองวันที่/สถานะ)
      // ป้องกันตัวกรองซ่อนรายการของรอบเก่า แล้วยืนยันรอบโดยขาดรายการ
      // ทีมทั่วไป: ยังโอนเฉพาะรายการที่ตรงกับตัวกรองปัจจุบันเหมือนเดิม
      const salariedTeam = isSalariedTeam(team);
      const payout = buildTeamPayout({
        team,
        records: salariedTeam
          ? records.filter((record) => (canonicalTeamName(record.teamName) || "ไม่ระบุ") === team)
          : list,
        extras: salariedTeam
          ? extraIncomes.filter((item) => (canonicalTeamName(item.teamName) || "ไม่ระบุ") === team)
          : extraList,
        now: new Date(),
      });
      const salaried = payout.salaried;
      const unpaidPressList = payout.outstanding.pressList;
      const unpaidExtraList = payout.outstanding.extraList;
      const unpaidAmount = payout.outstanding.total;
      const roundCanConfirm = payout.round.canConfirm;

      // ถึงกำหนดโอนตอนนี้ = รอบปัจจุบันที่ยืนยันได้แล้ว
      const pressDue = roundCanConfirm ? payout.round.pressList : [];
      const extraDue = roundCanConfirm ? payout.round.extraList : [];
      // รอรอบเงินเดือน = รายการค้างที่ยังไม่ถึงกำหนด
      const pressScheduled = salaried
        ? (roundCanConfirm ? payout.remaining.pressList : unpaidPressList)
        : [];
      const extraScheduled = salaried
        ? (roundCanConfirm ? payout.remaining.extraList : unpaidExtraList)
        : [];

      const dueSummary = roundCanConfirm ? payout.round : null;
      const duePressAmount = dueSummary ? dueSummary.pressNet : 0;
      const dueExtraGross = dueSummary ? dueSummary.extra.gross : 0;
      const dueExtraWht = dueSummary ? dueSummary.extra.wht : 0;
      const dueExtraNet = dueSummary ? dueSummary.extraNet : 0;
      const dueAmount = dueSummary ? dueSummary.total : 0;
      const scheduledAmount = salaried
        ? (roundCanConfirm ? payout.remaining.total : payout.outstanding.total)
        : 0;
      const nextPayday = salaried
        ? (roundCanConfirm ? payout.remaining.nextDate : payout.round.date)
        : null;

      const allTimestamps = [
        ...list.map((record) => toTS(record.createdAt)),
        ...extraList.map((item) => toTS(itemCreatedAt(item))),
      ];
      const latestTs = allTimestamps.length ? Math.max(...allTimestamps) : 0;
      const oldestTs = allTimestamps.length ? Math.min(...allTimestamps) : 0;

      const teamSettlements = settlements
        .filter((item) => canonicalTeamName(item.teamName) === team)
        .sort((a, b) =>
          toTS(b.status === "draft" ? (b.updatedAt || b.createdAt) : (b.paidAt || b.createdAt)) -
          toTS(a.status === "draft" ? (a.updatedAt || a.createdAt) : (a.paidAt || a.createdAt))
        );

      return {
        team,
        payout,
        realName: getTeamRealName(team, realNameByTeam),
        // พนักงานเงินเดือน + ดูเฉพาะที่ยังไม่โอน → แสดงรายการชุดเดียวกับที่ใช้คิดยอด (ไม่ขึ้นกับตัวกรองวันที่)
        list: salariedTeam && showUnpaidOnly ? payout.outstanding.pressList : list,
        extraList: salariedTeam && showUnpaidOnly ? payout.outstanding.extraList : extraList,
        payable,
        pressNet,
        baseFee,
        commission,
        wht,
        extraGross,
        extraWht,
        extraNet,
        quantity: salariedTeam && showUnpaidOnly ? payout.outstanding.quantity : quantity,
        salaried,
        unpaidPressList,
        unpaidExtraList,
        unpaidCount: unpaidPressList.length + unpaidExtraList.length,
        unpaidAmount,
        duePressList: pressDue,
        dueExtraList: extraDue,
        dueCount: pressDue.length + extraDue.length,
        duePressAmount,
        dueExtraGross,
        dueExtraWht,
        dueExtraNet,
        dueAmount,
        scheduledPressList: pressScheduled,
        scheduledExtraList: extraScheduled,
        scheduledCount: pressScheduled.length + extraScheduled.length,
        scheduledAmount,
        nextPayday,
        latestTs,
        oldestTs,
        settlements: teamSettlements,
      };
    });

    const queryText = teamQuery.trim().toLowerCase();
    if (queryText) {
      result = result.filter((group) =>
        `${group.team} ${group.realName || ""}`.toLowerCase().includes(queryText)
      );
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case "latest":
          return b.latestTs - a.latestTs;
        case "oldest":
          return a.oldestTs - b.oldestTs;
        case "teamAZ":
          return a.team.localeCompare(b.team, "th");
        case "unpaidDesc":
          return b.unpaidCount - a.unpaidCount;
        case "amountDesc":
        default:
          return (
            b.dueAmount - a.dueAmount ||
            b.scheduledAmount - a.scheduledAmount ||
            b.payable - a.payable
          );
      }
    });

    return result;
  }, [visibleRecords, visibleExtras, records, extraIncomes, showUnpaidOnly, teamQuery, sortBy, realNameByTeam, settlements]);

  const summary = useMemo(() => {
    const teamCount = groups.length;
    const recordCount = groups.reduce(
      (sum, group) => sum + group.list.length + group.extraList.length,
      0
    );
    const ticketCount = groups.reduce((sum, group) => sum + group.quantity, 0);
    const dueCount = groups.reduce((sum, group) => sum + group.dueCount, 0);
    const dueAmount = groups.reduce((sum, group) => sum + group.dueAmount, 0);
    const scheduledCount = groups.reduce((sum, group) => sum + group.scheduledCount, 0);
    const scheduledAmount = groups.reduce((sum, group) => sum + group.scheduledAmount, 0);
    const extraGross = groups.reduce((sum, group) => sum + group.extraGross, 0);
    const extraWht = groups.reduce((sum, group) => sum + group.extraWht, 0);

    return {
      teamCount,
      recordCount,
      ticketCount,
      dueCount,
      dueAmount,
      scheduledCount,
      scheduledAmount,
      extraGross,
      extraWht,
    };
  }, [groups]);

  const currentMonthRange = () => {
    const parts = getBangkokDateParts(new Date());
    const lastDay = new Date(parts.year, parts.month, 0).getDate();
    setDateFrom(`${parts.year}-${String(parts.month).padStart(2, "0")}-01`);
    setDateTo(
      `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`
    );
  };

  const clearDates = () => {
    setDateFrom("");
    setDateTo("");
  };

  const dateScopeLabel = useMemo(() => {
    if (!dateFrom && !dateTo) return "ทุกวันที่";
    if (dateFrom && dateTo) return `${formatDate(dateFrom)} – ${formatDate(dateTo)}`;
    if (dateFrom) return `ตั้งแต่ ${formatDate(dateFrom)}`;
    return `ถึง ${formatDate(dateTo)}`;
  }, [dateFrom, dateTo]);

  const syncPaidState = (group, settlementId = null) => {
    const pressPaths = new Set(group.duePressList.map((record) => record.ref?.path));
    const extraPaths = new Set(group.dueExtraList.map((item) => item.ref?.path));

    setRecords((previous) =>
      previous.map((record) =>
        pressPaths.has(record.ref?.path) ? { ...record, isPaid: true, settlementId } : record
      )
    );
    setExtraIncomes((previous) =>
      previous.map((item) =>
        extraPaths.has(item.ref?.path) ? { ...item, isPaid: true, settlementId } : item
      )
    );
  };

  const createSettlementPayload = (group, overrides = {}) => {
    const status = overrides.status || "paid";
    const now = new Date();
    const pressAccounting = (group.duePressList || []).reduce(
      (acc, record) => {
        const fee = calculateRecordFee(record);
        acc.beforeWht += Number(fee.teamBeforeTax ?? fee.beforeTax ?? ((fee.net || 0) + (fee.tax || 0)));
        acc.wht += Number(fee.tax || 0);
        acc.net += Number(fee.net || 0);
        return acc;
      },
      { beforeWht: 0, wht: 0, net: 0 }
    );

    return {
      teamName: group.team,
      teamRealName: group.realName || "",
      salaried: group.salaried,
      pressFeeAmount: roundMoney(group.duePressAmount),
      pressBeforeWhtAmount: roundMoney(pressAccounting.beforeWht),
      pressWhtAmount: roundMoney(pressAccounting.wht),
      extraGrossAmount: roundMoney(group.dueExtraGross),
      extraBeforeWhtAmount: roundMoney(group.dueExtraGross),
      extraWhtAmount: roundMoney(group.dueExtraWht),
      extraNetAmount: roundMoney(group.dueExtraNet),
      systemDueAmount: roundMoney(group.dueAmount),
      // เงินช่วยจากบริษัท (ค่าใช้จ่ายบริษัท) และค่าจ้างรวมของรอบ = ยอดจากระบบ + เงินช่วย
      // โอนเกินจากที่ทำได้ = บริษัทจ่ายเพิ่ม (ค่าใช้จ่ายบริษัท) คำนวณจากยอดโอนจริง + WHT + ประกันสังคม
      ...(() => {
        const result = calcCompanyTopUp({
          systemDueAmount: group.dueAmount,
          actualTransferAmount: overrides.actualTransferAmount ?? group.dueAmount,
          payrollWhtAmount: overrides.payrollWhtAmount || 0,
          socialSecurityAmount: overrides.socialSecurityAmount || 0,
        });
        return {
          companyTopUpAmount: result.topUp,
          underpaidAmount: result.shortfall,
          wageAmount: result.paidWage,
        };
      })(),
      pressRecordCount: (group.duePressList || []).length,
      extraIncomeCount: (group.dueExtraList || []).length,
      pressQuantity: (group.duePressList || []).reduce((sum, record) => sum + Number(record.quantity || 0), 0),
      pressBaseFeeAmount: roundMoney((group.duePressList || []).reduce((sum, record) => sum + Number(calculateRecordFee(record).baseFee || 0), 0)),
      pressCommissionAmount: roundMoney((group.duePressList || []).reduce((sum, record) => sum + Number(calculateRecordFee(record).deducted || 0), 0)),
      outstandingBeforeAmount: roundMoney(group.payout?.outstanding?.total ?? group.dueAmount),
      remainingAfterAmount: roundMoney(
        (group.payout?.outstanding?.total ?? group.dueAmount) - group.dueAmount
      ),
      payrollWhtAmount: roundMoney(overrides.payrollWhtAmount || 0),
      socialSecurityAmount: roundMoney(overrides.socialSecurityAmount || 0),
      actualTransferAmount: roundMoney(
        overrides.actualTransferAmount ?? group.dueAmount
      ),
      note: String(overrides.note || "").trim(),
      pressRecordPaths: group.duePressList.map((record) => record.ref?.path).filter(Boolean),
      extraIncomeIds: group.dueExtraList.map((item) => item.id).filter(Boolean),
      itemCount: group.dueCount,
      settlementRoundDate: group.salaryRoundDate || null,
      status,
      createdAt: overrides.createdAt || now,
      updatedAt: now,
      paidAt: status === "paid" ? (overrides.paidAt || now) : null,
      createdByUid: overrides.createdByUid || currentUser?.uid || "",
      createdByEmail: overrides.createdByEmail || currentUser?.email || "",
      updatedByUid: currentUser?.uid || "",
      updatedByEmail: currentUser?.email || "",
    };
  };

  // ยืนยันโอนแบบ transaction: อ่านทุกรายการใหม่จาก Firestore ก่อนเขียน
  // ถ้ามีรายการไหนถูกโอนแล้ว / ถูกผูกกับรอบโอนอื่นแล้ว จะยกเลิกทั้งชุด → กันโอนซ้ำ
  // (เช่น เปิดหน้าไว้ 2 แท็บ, แอดมิน 2 คนกดพร้อมกัน, หรือข้อมูลบนหน้าค้าง)
  const commitSettlementSafely = async (group, settlementRef, payload) => {
    const pressRefs = (group.duePressList || []).map((record) => record.ref).filter(Boolean);
    const extraRefs = (group.dueExtraList || []).map((item) => item.ref).filter(Boolean);
    const uniquePaths = new Set([...pressRefs, ...extraRefs].map((ref) => ref.path));
    if (uniquePaths.size !== pressRefs.length + extraRefs.length) {
      throw new Error("DUPLICATE_IN_ROUND");
    }

    const localConflicts = findAlreadySettled({
      pressPaths: pressRefs.map((ref) => ref.path),
      extraIds: (group.dueExtraList || []).map((item) => item.id),
      settlements,
      excludeSettlementId: settlementRef.id,
    });
    if (localConflicts.length) throw new Error("ALREADY_SETTLED");

    const paidAt = payload.paidAt || new Date();
    await runTransaction(db, async (transaction) => {
      const settlementSnap = await transaction.get(settlementRef);
      if (settlementSnap.exists() && settlementSnap.data()?.status === "paid") {
        throw new Error("ALREADY_SETTLED");
      }
      const snaps = await Promise.all(
        [...pressRefs, ...extraRefs].map((ref) => transaction.get(ref))
      );
      const conflict = snaps.find((snap) => {
        if (!snap.exists()) return true;
        const data = snap.data() || {};
        return data.isPaid === true || !!data.settlementId;
      });
      if (conflict) throw new Error("ALREADY_SETTLED");

      [...pressRefs, ...extraRefs].forEach((ref) =>
        transaction.update(ref, { isPaid: true, paidAt, settlementId: settlementRef.id })
      );
      transaction.set(settlementRef, payload, { merge: true });
    });
  };

  const settlementErrorMessage = (error) =>
    error?.message === "ALREADY_SETTLED" || error?.message === "DUPLICATE_IN_ROUND"
      ? "มีบางรายการถูกโอน/อยู่ในรอบโอนอื่นแล้ว ระบบยกเลิกการยืนยันทั้งชุดเพื่อกันโอนซ้ำ กรุณารีเฟรชหน้าแล้วตรวจอีกครั้ง"
      : "อัปเดตสถานะโอนไม่สำเร็จ กรุณาลองอีกครั้ง";

  const handleMarkPaidStandard = async () => {
    if (!confirmTeam?.dueCount || savingTeam) return;
    const group = confirmTeam;
    setSavingTeam(group.team);

    try {
      const settlementRef = doc(collection(db, "team-payment-settlements"));
      const payload = createSettlementPayload(group);
      await commitSettlementSafely(group, settlementRef, payload);
      await audit({ action: "settlement.paid", entityType: "settlement", entityId: settlementRef.id, title: `ยืนยันโอน ${group.team}`, before: null, after: payload, meta: { pressCount: group.duePressList.length, extraCount: group.dueExtraList.length } });

      syncPaidState(group, settlementRef.id);
      setSettlements((previous) => [
        ...previous,
        { id: settlementRef.id, ref: settlementRef, ...payload },
      ]);
      setDetailTeam(null);
      setConfirmTeam(null);
      showNotification?.(`บันทึกว่าโอนให้ ${formatTeamWithRealName(group.team, realNameByTeam)} แล้ว`);
    } catch (error) {
      console.error("Failed to mark paid", error);
      showNotification?.(settlementErrorMessage(error));
    } finally {
      setSavingTeam("");
    }
  };

  // รอบโอนของพนักงานเงินเดือน = payout.round (payday เก่าสุดที่ยังค้าง)
  // ถ้าถึงกำหนดแล้วยืนยันได้ ถ้ายังไม่ถึงเตรียมร่างล่วงหน้าได้ แต่ยังไม่เปลี่ยนสถานะ
  const buildSalarySettlementGroup = (group) => {
    if (!group?.salaried) return group;
    const round = group.payout?.round;
    if (!round || !round.count) {
      return { ...group, salaryRoundDate: null, salaryCanConfirm: false, salaryPreparingAhead: true };
    }

    return {
      ...group,
      duePressList: round.pressList,
      dueExtraList: round.extraList,
      dueCount: round.count,
      duePressAmount: round.pressNet,
      dueExtraGross: round.extra.gross,
      dueExtraWht: round.extra.wht,
      dueExtraNet: round.extraNet,
      dueAmount: round.total,
      dueQuantity: round.quantity,
      dueBaseFee: round.press.baseFee,
      dueCommission: round.press.commission,
      remainingAfterRound: group.payout.remaining,
      salarySupportAmount: calcSalarySupport(group.team, round.total),
      outstandingTotal: group.payout.outstanding.total,
      salaryRoundDate: round.date,
      salaryCanConfirm: round.canConfirm,
      salaryPreparingAhead: !round.canConfirm,
    };
  };

  const getSalaryDraftForGroup = (group) => {
    const settlementGroup = buildSalarySettlementGroup(group);
    if (!settlementGroup?.salaryRoundDate) return null;
    return group.settlements.find(
      (item) => item.status === "draft" &&
        isSameBangkokDay(item.settlementRoundDate, settlementGroup.salaryRoundDate)
    ) || null;
  };

  const openSalarySettlement = (group) => {
    const settlementGroup = buildSalarySettlementGroup(group);
    if (!settlementGroup?.dueCount) return;

    // แบบร่างต้องตรงกับรอบวันที่ 26 ของเดือนเดียวกันเท่านั้น
    // ป้องกันร่างเดือนก่อนถูกหยิบมาแก้ทับรอบเดือนใหม่
    const draft = getSalaryDraftForGroup(group);
    setSettlementTeam(settlementGroup);
    setSalaryDraft(draft);
    setSettlementForm({
      payrollWht: String(Number(draft?.payrollWhtAmount || 0)),
      // ไม่มีร่าง → เสนอประกันสังคม 5% ของค่าจ้างรอบนี้ (รวมเงินช่วยบริษัท) แก้ได้
      socialSecurity: String(
        draft
          ? Number(draft.socialSecurityAmount || 0)
          : calcSocialSecurity(Number(settlementGroup.dueAmount || 0) + Number(settlementGroup.salarySupportAmount || 0))
      ),
      actualTransfer: draft ? String(Number(draft.actualTransferAmount || 0)) : "",
      note: draft?.note || "",
    });
  };

  const requestMarkPaid = (group) => {
    if (!group) return;
    if (group.salaried) {
      if (!group.dueCount && !group.scheduledCount) return;
      openSalarySettlement(group);
    } else {
      if (!group.dueCount) return;
      setConfirmTeam(group);
    }
  };

  const payrollWhtValue = roundMoney(settlementForm.payrollWht);
  const socialSecurityValue = roundMoney(settlementForm.socialSecurity);
  const salarySuggestedTransfer = settlementTeam
    ? Math.max(0, roundMoney(settlementTeam.dueAmount + Number(settlementTeam.salarySupportAmount || 0) - payrollWhtValue - socialSecurityValue))
    : 0;

  const getSalaryFormValues = () => {
    const actualTransfer = settlementForm.actualTransfer === ""
      ? salarySuggestedTransfer
      : roundMoney(settlementForm.actualTransfer);

    if (actualTransfer < 0 || payrollWhtValue < 0 || socialSecurityValue < 0) {
      showNotification?.("ยอดหักและยอดโอนจริงต้องไม่ติดลบ");
      return null;
    }

    return {
      payrollWhtAmount: payrollWhtValue,
      socialSecurityAmount: socialSecurityValue,
      actualTransferAmount: actualTransfer,
      note: settlementForm.note,
    };
  };

  const upsertSettlementState = (ref, payload) => {
    setSettlements((previous) => {
      const exists = previous.some((item) => item.ref?.path === ref.path);
      if (exists) {
        return previous.map((item) =>
          item.ref?.path === ref.path ? { ...item, ...payload, id: item.id || ref.id, ref } : item
        );
      }
      return [...previous, { id: ref.id, ref, ...payload }];
    });
  };

  const handleSaveSalaryDraft = async () => {
    if (!settlementTeam?.dueCount || savingTeam) return;
    const values = getSalaryFormValues();
    if (!values) return;

    const group = settlementTeam;
    setSavingTeam(group.team);
    try {
      const settlementRef = salaryDraft?.ref || doc(collection(db, "team-payment-settlements"));
      const payload = createSettlementPayload(group, {
        ...values,
        status: "draft",
        createdAt: salaryDraft?.createdAt || new Date(),
        createdByUid: salaryDraft?.createdByUid || currentUser?.uid || "",
        createdByEmail: salaryDraft?.createdByEmail || currentUser?.email || "",
      });
      await setDoc(settlementRef, payload, { merge: true });
      await audit({ action: "settlement.draft", entityType: "settlement", entityId: settlementRef.id, title: `รอบโอน ${group.team}`, before: salaryDraft, after: payload });
      const nextDraft = { id: settlementRef.id, ref: settlementRef, ...payload };
      setSalaryDraft(nextDraft);
      upsertSettlementState(settlementRef, payload);
      showNotification?.(`บันทึกแบบร่างรอบโอนของ ${formatTeamWithRealName(group.team, realNameByTeam)} แล้ว`);
      setSettlementTeam(null);
      // ปิดหน้ารายละเอียดเพื่อให้เปิดใหม่ได้ข้อมูลร่างล่าสุด (กันสร้างร่างซ้ำ)
      setDetailTeam(null);
    } catch (error) {
      console.error("Failed to save salary settlement draft", error);
      showNotification?.("บันทึกแบบร่างรอบโอนไม่สำเร็จ");
    } finally {
      setSavingTeam("");
    }
  };

  const handleConfirmSalaryPaid = async () => {
    if (!settlementTeam?.dueCount || savingTeam) return;
    if (!settlementTeam.salaryCanConfirm) {
      showNotification?.(`รอบนี้ยืนยันโอนได้วันที่ ${formatPayday(settlementTeam.salaryRoundDate)}`);
      return;
    }
    const values = getSalaryFormValues();
    if (!values) return;

    const group = settlementTeam;
    setSavingTeam(group.team);
    try {
      const settlementRef = salaryDraft?.ref || doc(collection(db, "team-payment-settlements"));
      const payload = createSettlementPayload(group, {
        ...values,
        status: "paid",
        createdAt: salaryDraft?.createdAt || new Date(),
        createdByUid: salaryDraft?.createdByUid || currentUser?.uid || "",
        createdByEmail: salaryDraft?.createdByEmail || currentUser?.email || "",
        paidAt: new Date(),
      });
      await commitSettlementSafely(group, settlementRef, payload);
      await audit({ action: "settlement.paid", entityType: "settlement", entityId: settlementRef.id, title: `ยืนยันโอน ${group.team}`, before: salaryDraft, after: payload, meta: { pressCount: group.duePressList.length, extraCount: group.dueExtraList.length } });

      syncPaidState(group, settlementRef.id);
      upsertSettlementState(settlementRef, payload);
      setDetailTeam(null);
      setSettlementTeam(null);
      setSalaryDraft(null);
      showNotification?.(`ยืนยันโอนให้ ${formatTeamWithRealName(group.team, realNameByTeam)} แล้ว`);
    } catch (error) {
      console.error("Failed to confirm salary settlement", error);
      showNotification?.(settlementErrorMessage(error));
    } finally {
      setSavingTeam("");
    }
  };

  const openAddExtra = () => {
    setEditingExtra(null);
    setExtraForm({
      teamName: "",
      category: "ค่าคอม",
      description: "",
      amount: "",
      date: todayBangkokInput(),
      paymentStatus: "unpaid",
    });
    setExtraModalOpen(true);
  };

  const openEditExtra = (item) => {
    const amounts = extraAmounts(item);
    const parts = getBangkokDateParts(itemCreatedAt(item));
    setEditingExtra(item);
    setExtraForm({
      teamName: canonicalTeamName(item.teamName),
      category: item.category || "ค่าคอม",
      description: item.description || "",
      amount: String(amounts.net),
      date: `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`,
      paymentStatus: item.isPaid ? "paid" : "unpaid",
    });
    setExtraModalOpen(true);
  };

  // บริษัทออกภาษีแทนตลอดไป: ทีมได้รับเต็มยอดที่กรอก, เงินได้ = ยอด ÷ 0.97, WHT = เงินได้ − ยอด
  const extraPreview = grossUpExtraIncome(extraForm.amount, EXTRA_WHT_RATE);
  const extraPreviewNet = extraPreview.net;
  const extraPreviewWht = extraPreview.wht;
  const extraPreviewGross = extraPreview.gross;

  const handleSaveExtraIncome = async () => {
    const teamName = canonicalTeamName(extraForm.teamName);
    if (!teamName || teamName === OWNER_TEAM) {
      showNotification?.("กรุณาเลือกทีมที่ต้องการเพิ่มรายได้");
      return;
    }
    if (!(extraPreviewNet > 0)) {
      showNotification?.("กรุณากรอกจำนวนเงินมากกว่า 0 บาท");
      return;
    }
    if (extraForm.category === "อื่นๆ" && !extraForm.description.trim()) {
      showNotification?.("กรุณาระบุรายละเอียดของรายได้อื่นๆ");
      return;
    }

    setSavingExtra(true);
    try {
      const createdAt = dateInputToBangkokDate(extraForm.date);
      // แก้รายการเก่า (คิด WHT แบบเดิม) โดยไม่เปลี่ยนยอด → คงตัวเลขเงินเดิมไว้ ไม่คิด WHT ใหม่
      const keepLegacyAmounts =
        !!editingExtra &&
        editingExtra.whtMethod !== EXTRA_WHT_METHOD &&
        roundMoney(extraAmounts(editingExtra).net) === extraPreviewNet;
      const amountFields = keepLegacyAmounts
        ? {}
        : {
            grossAmount: extraPreviewGross,
            companyCostAmount: extraPreviewGross,
            teamReceivesAmount: extraPreviewNet,
            whtBaseAmount: extraPreview.whtBase,
            whtMethod: EXTRA_WHT_METHOD,
            taxBorneByCompany: true,
            whtRate: EXTRA_WHT_RATE,
            withholdingAmount: extraPreviewWht,
            netAmount: extraPreviewNet,
          };
      const payload = {
        teamName,
        teamRealName: getTeamRealName(teamName, realNameByTeam),
        category: extraForm.category,
        description: extraForm.description.trim(),
        ...amountFields,
        createdAt,
        updatedAt: new Date(),
        isPaid: extraForm.paymentStatus === "paid",
        paidAt: extraForm.paymentStatus === "paid"
          ? (editingExtra?.paidAt || new Date())
          : null,
        createdByUid: editingExtra?.createdByUid || currentUser?.uid || "",
        createdByEmail: editingExtra?.createdByEmail || currentUser?.email || "",
        updatedByUid: currentUser?.uid || "",
        updatedByEmail: currentUser?.email || "",
      };

      if (editingExtra?.ref) {
        await updateDoc(editingExtra.ref, payload);
        await audit({ action: "extra-income.update", entityType: "extra-income", entityId: editingExtra.id || editingExtra.ref.id, title: payload.description || payload.category, before: editingExtra, after: payload });
        setExtraIncomes((previous) =>
          previous.map((item) =>
            item.ref?.path === editingExtra.ref.path ? { ...item, ...payload } : item
          )
        );
        showNotification?.("แก้ไขรายได้พิเศษเรียบร้อย");
      } else {
        const ref = doc(collection(db, "team-extra-income"));
        await setDoc(ref, payload);
        await audit({ action: "extra-income.create", entityType: "extra-income", entityId: ref.id, title: payload.description || payload.category, after: payload });
        setExtraIncomes((previous) => [
          ...previous,
          { id: ref.id, ref, ...payload },
        ]);
        showNotification?.(`เพิ่ม${extraForm.category}ให้ ${formatTeamWithRealName(teamName, realNameByTeam)} แล้ว`);
      }

      setExtraModalOpen(false);
      setEditingExtra(null);
      setDetailTeam(null);
    } catch (error) {
      console.error("Failed to save extra income", error);
      showNotification?.("บันทึกรายได้พิเศษไม่สำเร็จ");
    } finally {
      setSavingExtra(false);
    }
  };

  const openOwnerEdit = (record) => {
    setOwnerEditRecord(record);
    setOwnerFeeInput(String(calculateRecordFee(record).feePer || record.feePerTicket || 0));
    setOwnerQtyInput(String(Number(record.quantity || 1)));
    setOwnerDeleteConfirm(false);
  };

  // ลบค่ากดของยยที่กรอกผิด (เช่น ไม่ได้กดงานนี้จริง)
  // เฉพาะรายการของยยเท่านั้น — ไม่กระทบยอดโอนทีม · เก็บข้อมูลเดิมทั้งหมดไว้ใน Audit Log
  const handleDeleteOwnerRecord = async () => {
    if (!ownerEditRecord?.ref || savingOwnerFee) return;
    if (canonicalTeamName(ownerEditRecord.teamName) !== OWNER_TEAM) return;
    if (!ownerDeleteConfirm) {
      setOwnerDeleteConfirm(true);
      return;
    }
    setSavingOwnerFee(true);
    try {
      const { ref, ...snapshot } = ownerEditRecord;
      // เขียน Audit Log ก่อนลบ และถ้าเขียนไม่สำเร็จจะไม่ลบ (กันข้อมูลหายถาวร)
      await writeAuditLog({ user: currentUser, action: "owner-fee.delete", entityType: "press-fee", entityId: ownerEditRecord.id || ref.id, title: ownerEditRecord.eventName || "ค่ากดของยย", before: { ...snapshot, path: ref.path }, after: null });
      await deleteDoc(ref);
      setRecords((previous) => previous.filter((record) => record.ref?.path !== ref.path));
      setOwnerEditRecord(null);
      setOwnerDeleteConfirm(false);
      showNotification?.("ลบรายการค่ากดของยยแล้ว (ข้อมูลเดิมเก็บไว้ใน Audit Log)");
    } catch (error) {
      console.error("Failed to delete owner record", error);
      showNotification?.("ลบรายการไม่สำเร็จ");
    } finally {
      setSavingOwnerFee(false);
    }
  };

  const handleSaveOwnerFee = async () => {
    if (!ownerEditRecord?.ref || savingOwnerFee) return;
    const feePerTicket = roundMoney(ownerFeeInput);
    const quantity = Number(ownerQtyInput);
    if (feePerTicket < 0) {
      showNotification?.("ค่ากดต้องไม่ติดลบ");
      return;
    }
    if (!Number.isInteger(quantity) || quantity < 1) {
      showNotification?.("จำนวนใบต้องเป็นจำนวนเต็มตั้งแต่ 1 ใบ");
      return;
    }

    const totalFee = roundMoney(feePerTicket * quantity);
    const patch = {
      feePerTicket,
      quantity,
      totalFee,
      commissionRate: 0,
      totalDeducted: 0,
      teamBeforeTax: totalFee,
      applyWht: false,
      withholding3pct: 0,
      netIncome: totalFee,
      calculationSnapshot: true,
      updatedAt: new Date(),
      updatedByUid: currentUser?.uid || "",
      updatedByEmail: currentUser?.email || "",
    };

    setSavingOwnerFee(true);
    try {
      await updateDoc(ownerEditRecord.ref, patch);
      await audit({ action: "owner-fee.update", entityType: "press-fee", entityId: ownerEditRecord.id || ownerEditRecord.ref.id, title: ownerEditRecord.eventName || "ค่ากดของยย", before: ownerEditRecord, after: { ...ownerEditRecord, ...patch } });
      setRecords((previous) =>
        previous.map((record) =>
          record.ref?.path === ownerEditRecord.ref.path ? { ...record, ...patch } : record
        )
      );
      setOwnerEditRecord(null);
      showNotification?.("แก้ไขค่ากดของยยเรียบร้อย");
    } catch (error) {
      console.error("Failed to update owner fee", error);
      showNotification?.("แก้ไขค่ากดไม่สำเร็จ");
    } finally {
      setSavingOwnerFee(false);
    }
  };

  const openSettlementEdit = (settlement) => {
    setEditingSettlement(settlement);
    setSettlementEditForm({
      payrollWht: String(Number(settlement.payrollWhtAmount || 0)),
      socialSecurity: String(Number(settlement.socialSecurityAmount || 0)),
      actualTransfer: String(Number(settlement.actualTransferAmount || 0)),
      note: settlement.note || "",
    });
  };

  const handleUpdateSettlement = async () => {
    if (!editingSettlement?.ref || savingSettlement) return;
    const payrollWhtAmount = roundMoney(settlementEditForm.payrollWht);
    const socialSecurityAmount = roundMoney(settlementEditForm.socialSecurity);
    const actualTransferAmount = roundMoney(settlementEditForm.actualTransfer);
    if (payrollWhtAmount < 0 || socialSecurityAmount < 0 || actualTransferAmount < 0) {
      showNotification?.("ยอดเงินต้องไม่ติดลบ");
      return;
    }

    const patch = {
      ...(() => {
        const result = calcCompanyTopUp({
          systemDueAmount: editingSettlement.systemDueAmount,
          actualTransferAmount,
          payrollWhtAmount,
          socialSecurityAmount,
        });
        return { companyTopUpAmount: result.topUp, underpaidAmount: result.shortfall, wageAmount: result.paidWage };
      })(),
      payrollWhtAmount,
      socialSecurityAmount,
      actualTransferAmount,
      note: settlementEditForm.note.trim(),
      updatedAt: new Date(),
      updatedByUid: currentUser?.uid || "",
      updatedByEmail: currentUser?.email || "",
    };

    setSavingSettlement(true);
    try {
      await updateDoc(editingSettlement.ref, patch);
      await audit({ action: "settlement.update", entityType: "settlement", entityId: editingSettlement.id || editingSettlement.ref.id, title: `แก้รอบโอน ${editingSettlement.teamName || ""}`, before: editingSettlement, after: { ...editingSettlement, ...patch } });
      setSettlements((previous) =>
        previous.map((item) =>
          item.ref?.path === editingSettlement.ref.path ? { ...item, ...patch } : item
        )
      );
      setEditingSettlement(null);
      setDetailTeam(null);
      showNotification?.("อัปเดตข้อมูลรอบโอนเรียบร้อย");
    } catch (error) {
      console.error("Failed to update settlement", error);
      showNotification?.("อัปเดตรอบโอนไม่สำเร็จ");
    } finally {
      setSavingSettlement(false);
    }
  };

  return (
    <div className="payment-page">
      <section className="payment-hero">
        <div>
          <div className="payment-eyebrow">PAYMENT CENTER</div>
          <h1>โอนค่ากด & รายได้ทีม</h1>
          <p>เช็กยอด โอนให้ถูกคน และเก็บรายการหักของรอบเงินเดือนไว้ครบ</p>
        </div>
        <div className="payment-hero-actions">
          <button className="payment-add-income-btn" type="button" onClick={openAddExtra}>
            <span>＋</span> เพิ่มรายได้ทีม
          </button>
        </div>
      </section>

      <section className="payment-summary-grid" aria-label="สรุปยอด">
        <div className="payment-summary-card payment-summary-card--primary">
          <span className="payment-summary-label">ยอดที่ต้องโอนตอนนี้</span>
          <strong>{money(summary.dueAmount)}</strong>
          <small>{fmt(summary.dueCount)} รายการถึงกำหนด · ไม่รวมยย</small>
        </div>
        <div className="payment-summary-card payment-summary-card--salary">
          <span className="payment-summary-label">รอรอบเงินเดือน</span>
          <strong>{money(summary.scheduledAmount)}</strong>
          <small>{fmt(summary.scheduledCount)} รายการ · โอนทุกวันที่ 26</small>
        </div>
        <div className="payment-summary-card payment-summary-card--extra">
          <span className="payment-summary-label">ต้นทุนบริษัทรายได้พิเศษ</span>
          <strong>{money(summary.extraGross)}</strong>
          <small>ทีมได้รับ {money(groups.reduce((sum, group) => sum + group.extraNet, 0))} + WHT เฉพาะรายได้พิเศษ {money(summary.extraWht)}</small>
        </div>
        <div className="payment-summary-card">
          <span className="payment-summary-label">ขอบเขตข้อมูล</span>
          <strong className="payment-summary-date">{dateScopeLabel}</strong>
          <small>{showUnpaidOnly ? "เฉพาะที่ยังไม่โอน" : "รวมรายการที่โอนแล้ว"}</small>
        </div>
      </section>

      <section className="payment-toolbar-card">
        <div className="payment-toolbar-top">
          <label className="payment-search">
            <span>⌕</span>
            <input
              value={teamQuery}
              onChange={(event) => setTeamQuery(event.target.value)}
              placeholder="ค้นหาชื่อทีม หรือชื่อจริง"
            />
          </label>

          <div className="payment-segmented" role="group" aria-label="สถานะรายการ">
            <button
              type="button"
              className={showUnpaidOnly ? "is-active" : ""}
              onClick={() => setShowUnpaidOnly(true)}
            >
              รอโอน
            </button>
            <button
              type="button"
              className={!showUnpaidOnly ? "is-active" : ""}
              onClick={() => setShowUnpaidOnly(false)}
            >
              ทั้งหมด
            </button>
          </div>

          <label className="payment-sort">
            <span>เรียง</span>
            <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
              <option value="amountDesc">ยอดค้างมากสุด</option>
              <option value="latest">กรอกล่าสุด</option>
              <option value="oldest">กรอกเก่าสุด</option>
              <option value="unpaidDesc">รายการค้างมากสุด</option>
              <option value="teamAZ">ชื่อทีม ก → ฮ</option>
            </select>
          </label>
        </div>

        <div className="payment-date-row">
          <div className="payment-date-fields">
            <label>
              <span>ตั้งแต่</span>
              <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
            </label>
            <span className="payment-date-divider">→</span>
            <label>
              <span>ถึง</span>
              <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
            </label>
          </div>
          <div className="payment-date-actions">
            <button type="button" onClick={currentMonthRange}>เดือนนี้</button>
            <button type="button" onClick={clearDates} disabled={!dateFrom && !dateTo}>ล้างวันที่</button>
          </div>
        </div>
      </section>

      {/* ค่ากดของยยไม่ต้องโอน → ไม่แสดงในหน้าโอนค่ากด (แก้/ลบได้ที่หน้าประวัติ · ยอดดูที่การเงิน & รายงาน) */}

      <div className="payment-section-head">
        <div>
          <h2>{showUnpaidOnly ? "รายการที่รอโอน" : "รายการทั้งหมด"}</h2>
          <p>{groups.length ? `${fmt(groups.length)} ทีม · แสดงชื่อจริงเพื่อใช้ตรวจบัญชีก่อนโอน` : "ยังไม่มีข้อมูลตามตัวกรอง"}</p>
        </div>
      </div>

      {loading ? (
        <div className="payment-empty">
          <div className="payment-loader" />
          <strong>กำลังโหลดข้อมูล</strong>
          <span>รอสักครู่</span>
        </div>
      ) : groups.length === 0 ? (
        <div className="payment-empty">
          <div className="payment-empty-icon">✓</div>
          <strong>{showUnpaidOnly ? "ไม่มีรายการค้างโอน" : "ไม่พบรายการ"}</strong>
          <span>ลองเปลี่ยนชื่อทีม ช่วงวันที่ หรือตัวกรองด้านบน</span>
        </div>
      ) : (
        <section className="payment-team-list">
          {groups.map((group) => {
            const allPaid = group.unpaidCount === 0;
            const waitingSalaryRound = group.salaried && group.dueCount === 0 && group.scheduledCount > 0;
            const hasDueNow = group.dueCount > 0;
            return (
              <article className={`payment-team-card ${group.salaried ? "payment-team-card--salary" : ""}`} key={group.team}>
                <div className="payment-team-main">
                  <div className="payment-team-avatar">{group.team.slice(0, 2)}</div>
                  <div className="payment-team-info">
                    <div className="payment-team-name-row">
                      <h3>{group.team}</h3>
                      {allPaid ? (
                        <span className="payment-status payment-status--paid">โอนแล้ว</span>
                      ) : waitingSalaryRound ? (
                        <span className="payment-status payment-status--salary">รอรอบ {formatPayday(group.nextPayday)}</span>
                      ) : group.salaried && hasDueNow ? (
                        <span className="payment-status payment-status--due">ถึงรอบโอน {fmt(group.dueCount)}</span>
                      ) : (
                        <span className="payment-status payment-status--pending">รอโอน {fmt(group.unpaidCount)}</span>
                      )}
                    </div>
                    <div className={`payment-real-name-line ${group.realName ? "has-name" : "missing-name"}`}>
                      {group.realName ? (
                        <><span>ชื่อจริง</span><strong>{group.realName}</strong></>
                      ) : (
                        <><span>⚠</span><strong>ยังไม่ได้กรอกชื่อจริง</strong></>
                      )}
                    </div>
                    <div className="payment-team-meta">
                      {/* พนักงานเงินเดือนคิดยอดจากข้อมูลทั้งหมด → นับรายการให้ตรงกับยอดเงิน */}
                      <span>{fmt(group.salaried && showUnpaidOnly ? group.payout.outstanding.press.count : group.list.length)} ค่ากด</span>
                      {(group.salaried && showUnpaidOnly ? group.payout.outstanding.extra.count : group.extraList.length) > 0 && (
                        <><span>•</span><span>{fmt(group.salaried && showUnpaidOnly ? group.payout.outstanding.extra.count : group.extraList.length)} รายได้พิเศษ</span></>
                      )}
                      <span>•</span>
                      <span>{fmt(group.salaried && showUnpaidOnly ? group.payout.outstanding.quantity : group.quantity)} ใบ</span>
                      {group.salaried && (
                        <><span>•</span><span className="payment-salary-meta">พนักงานเงินเดือน · วันที่ 26</span></>
                      )}
                    </div>
                    {group.salaried && hasDueNow && group.scheduledCount > 0 && (
                      <div className="payment-next-round-note">
                        รอบถัดไป {formatPayday(group.nextPayday)} · {fmt(group.scheduledCount)} รายการ · {money(group.scheduledAmount)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="payment-team-amount">
                  {allPaid ? (
                    <>
                      <span>ยอดสุทธิ (โอนแล้ว)</span>
                      <strong>{money(group.payable)}</strong>
                    </>
                  ) : (
                    <>
                      <span>
                        {group.salaried
                          ? (group.payout.round.canConfirm
                              ? `ยอดโอนรอบ ${formatPayday(group.payout.round.date)}`
                              : `เตรียมรอบ ${formatPayday(group.payout.round.date)}`)
                          : "ยอดที่จะโอนรอบนี้"}
                      </span>
                      <strong>{money(group.payout.round.total)}</strong>
                      <small>
                        ค่ากด {money(group.payout.round.pressNet)}
                        {group.payout.round.extraNet > 0 && <> + รายได้พิเศษ {money(group.payout.round.extraNet)}</>}
                      </small>
                      {group.payout.remaining.count > 0 && (
                        <small className="payment-amount-remaining">
                          ค้างรวม {money(group.payout.outstanding.total)} · เหลือหลังรอบนี้ {money(group.payout.remaining.total)}
                        </small>
                      )}
                    </>
                  )}
                </div>

                <div className="payment-team-actions">
                  <button className="payment-ghost-btn" type="button" onClick={() => setDetailTeam(group)}>
                    ดูรายละเอียด
                  </button>
                  <button
                    className="payment-paid-btn"
                    type="button"
                    disabled={(!hasDueNow && !(group.salaried && group.scheduledCount > 0)) || savingTeam === group.team}
                    onClick={() => requestMarkPaid(group)}
                  >
                    {savingTeam === group.team
                      ? "กำลังบันทึก…"
                      : allPaid
                        ? "โอนแล้ว"
                        : group.salaried
                          ? (getSalaryDraftForGroup(group) ? "แก้ร่างรอบโอน" : hasDueNow ? "บันทึกรอบโอน" : "เตรียมยอดก่อนโอน")
                          : "ทำเครื่องหมายว่าโอนแล้ว"}
                  </button>
                </div>
              </article>
            );
          })}
        </section>
      )}

      {detailTeam && (
        <div className="payment-modal-overlay" onMouseDown={() => setDetailTeam(null)}>
          <div className="payment-modal payment-detail-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="payment-modal-head">
              <div>
                <span className="payment-modal-kicker">TEAM PAYMENT</span>
                <h3>{detailTeam.team}</h3>
                {detailTeam.realName ? (
                  <p className="payment-modal-real-name">ชื่อจริง: {detailTeam.realName}</p>
                ) : (
                  <p className="payment-modal-real-name payment-modal-real-name--missing">⚠ ยังไม่ได้กรอกชื่อจริง</p>
                )}
                <p>{fmt(detailTeam.list.length)} ค่ากด · {fmt(detailTeam.extraList.length)} รายได้พิเศษ · {fmt(detailTeam.quantity)} ใบ</p>
              </div>
              <button className="payment-modal-close" type="button" onClick={() => setDetailTeam(null)}>×</button>
            </div>

            {detailTeam.salaried && (
              <div className="payment-salary-banner">
                <div>
                  <strong>พนักงานเงินเดือน</strong>
                  <span>เตรียม WHT / ประกันสังคม / ยอดโอนจริงได้ตั้งแต่ก่อนวันที่ 26 แล้วค่อยยืนยันหลังโอนจริง</span>
                </div>
                <span className="payment-salary-banner-date">
                  {detailTeam.dueCount > 0 ? "ถึงรอบโอนแล้ว" : `รอบถัดไป ${formatPayday(detailTeam.nextPayday)}`}
                </span>
              </div>
            )}

            <div className="payment-detail-section-label">ค้างโอนทั้งหมด ({detailTeam.salaried ? "ทุกวันที่ · พนักงานเงินเดือนคิดรอบจากข้อมูลทั้งหมด" : dateScopeLabel})</div>
            <div className="payment-detail-summary payment-detail-summary--five">
              <div><span>ค่ากดก่อนหัก</span><strong>{money(detailTeam.payout.outstanding.press.baseFee)}</strong><small>{fmt(detailTeam.payout.outstanding.press.count)} รายการ · {fmt(detailTeam.payout.outstanding.quantity)} ใบ</small></div>
              <div><span>หักค่าคอมร้าน{detailTeam.payout.outstanding.press.wht > 0 ? " + WHT" : ""}</span><strong>− {money(detailTeam.payout.outstanding.press.commission + detailTeam.payout.outstanding.press.wht)}</strong></div>
              <div><span>ค่ากดสุทธิค้างโอน</span><strong>{money(detailTeam.payout.outstanding.pressNet)}</strong></div>
              <div><span>รายได้พิเศษสุทธิค้างโอน</span><strong>{money(detailTeam.payout.outstanding.extraNet)}</strong><small>{fmt(detailTeam.payout.outstanding.extra.count)} รายการ · บริษัทออก WHT แยก</small></div>
              <div className="is-net"><span>ยอดค้างโอนรวม</span><strong>{money(detailTeam.payout.outstanding.total)}</strong></div>
            </div>

            {detailTeam.payout.round.count > 0 && (
              <div className="payment-detail-round">
                <div className="is-round">
                  <span>
                    {detailTeam.salaried
                      ? `ยอดที่จะโอนรอบ ${formatPayday(detailTeam.payout.round.date)}${detailTeam.payout.round.canConfirm ? "" : " (ยังไม่ถึงกำหนด)"}`
                      : "ยอดที่จะโอนรอบนี้"}
                  </span>
                  <strong>{money(detailTeam.payout.round.total)}</strong>
                  <small>
                    ค่ากด {fmt(detailTeam.payout.round.press.count)} รายการ / {fmt(detailTeam.payout.round.quantity)} ใบ = {money(detailTeam.payout.round.pressNet)}
                    {detailTeam.payout.round.extra.count > 0 && <> · รายได้พิเศษ {fmt(detailTeam.payout.round.extra.count)} รายการ = {money(detailTeam.payout.round.extraNet)}</>}
                  </small>
                </div>
                <div>
                  <span>เหลือค้างหลังรอบนี้</span>
                  <strong>{money(detailTeam.payout.remaining.total)}</strong>
                  <small>
                    {detailTeam.payout.remaining.count > 0
                      ? <>ค่ากด {fmt(detailTeam.payout.remaining.press.count)} รายการ / {fmt(detailTeam.payout.remaining.quantity)} ใบ = {money(detailTeam.payout.remaining.pressNet)}{detailTeam.payout.remaining.extra.count > 0 && <> · รายได้พิเศษ {money(detailTeam.payout.remaining.extraNet)}</>}{detailTeam.payout.remaining.nextDate && <> · รอบ {formatPayday(detailTeam.payout.remaining.nextDate)}</>}</>
                      : "ไม่มี"}
                  </small>
                </div>
              </div>
            )}

            {detailTeam.list.length > 0 && (
              <section className="payment-detail-block">
                <div className="payment-subsection-head">
                  <div><strong>ค่ากด</strong><span>{fmt(detailTeam.list.length)} รายการ</span></div>
                </div>
                <div className="payment-record-list">
                  {detailTeam.list
                    .slice()
                    .sort((a, b) => toTS(b.createdAt) - toTS(a.createdAt))
                    .map((record) => {
                      const fee = calculateRecordFee(record);
                      return (
                        <div className="payment-record-row" key={record.ref?.path || record.id}>
                          <div className="payment-record-copy">
                            <div className="payment-record-title">
                              <strong>{record.eventName || "ไม่ระบุชื่องาน"}</strong>
                              {record.isPaid ? (
                                <span className="payment-status payment-status--paid">โอนแล้ว</span>
                              ) : detailTeam.salaried ? (() => {
                                const payday = getSalaryPayday(record.createdAt);
                                const inRound = detailTeam.payout.round.pressList.some((x) => x.ref?.path === record.ref?.path);
                                const label = inRound
                                  ? (detailTeam.payout.round.canConfirm ? "ถึงรอบโอน" : `รอบนี้ · ${formatPayday(payday)}`)
                                  : `รอ ${formatPayday(payday)}`;
                                return <span className={`payment-status ${inRound && detailTeam.payout.round.canConfirm ? "payment-status--due" : "payment-status--salary"}`}>{label}</span>;
                              })() : (
                                <span className="payment-status payment-status--pending">รอโอน</span>
                              )}
                            </div>
                            <div className="payment-record-meta">
                              <span>{record.zone || "ไม่ระบุโซน"}</span><span>•</span>
                              <span>{fmt(record.quantity)} ใบ</span><span>•</span>
                              <span>{formatDateTime(record.createdAt)}</span>
                            </div>
                          </div>
                          <strong className="payment-record-amount">{money(fee.net)}</strong>
                        </div>
                      );
                    })}
                </div>
              </section>
            )}

            {detailTeam.extraList.length > 0 && (
              <section className="payment-detail-block payment-extra-block">
                <div className="payment-subsection-head">
                  <div><strong>รายได้พิเศษ / ค่าคอม</strong><span>ทีมได้รับเต็มตามยอดที่กรอก · บริษัทออก WHT เพิ่ม</span></div>
                </div>
                <div className="payment-record-list">
                  {detailTeam.extraList
                    .slice()
                    .sort((a, b) => toTS(itemCreatedAt(b)) - toTS(itemCreatedAt(a)))
                    .map((item) => {
                      const amounts = extraAmounts(item);
                      return (
                        <div className="payment-record-row payment-extra-row" key={item.id}>
                          <div className="payment-record-copy">
                            <div className="payment-record-title">
                              <strong>{item.category || "รายได้พิเศษ"}</strong>
                              {item.isPaid ? (
                                <span className="payment-status payment-status--paid">โอนแล้ว</span>
                              ) : detailTeam.salaried ? (() => {
                                const payday = getSalaryPayday(itemCreatedAt(item));
                                const inRound = detailTeam.payout.round.extraList.some((x) => x.ref?.path === item.ref?.path);
                                const label = inRound
                                  ? (detailTeam.payout.round.canConfirm ? "ถึงรอบโอน" : `รอบนี้ · ${formatPayday(payday)}`)
                                  : `รอ ${formatPayday(payday)}`;
                                return <span className={`payment-status ${inRound && detailTeam.payout.round.canConfirm ? "payment-status--due" : "payment-status--salary"}`}>{label}</span>;
                              })() : (
                                <span className="payment-status payment-status--pending">รอโอน</span>
                              )}
                            </div>
                            <div className="payment-record-meta">
                              {item.description && <><span>{item.description}</span><span>•</span></>}
                              <span>{formatDate(itemCreatedAt(item))}</span><span>•</span>
                              <span>ทีมได้รับ {money(amounts.net)}</span><span>•</span>
                              <span>WHT บริษัทออก {money(amounts.wht)}</span><span>•</span>
                              <span>ต้นทุนรวม {money(amounts.gross)}</span>
                            </div>
                          </div>
                          <div className="payment-record-side-actions">
                            <strong className="payment-record-amount">{money(amounts.net)}</strong>
                            {!item.isPaid && (
                              <button className="payment-mini-btn" type="button" onClick={() => openEditExtra(item)}>แก้ไข</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </section>
            )}

            {detailTeam.salaried && detailTeam.settlements.length > 0 && (
              <section className="payment-detail-block payment-settlement-block">
                <div className="payment-subsection-head">
                  <div><strong>ประวัติรอบโอน</strong><span>แก้ยอดโอนจริง / WHT / ประกันสังคมย้อนหลังได้</span></div>
                </div>
                <div className="payment-settlement-list">
                  {detailTeam.settlements.slice(0, 12).map((settlement) => {
                    const isDraft = settlement.status === "draft";
                    return (
                      <div className="payment-settlement-row" key={settlement.id}>
                        <div>
                          <strong>{isDraft ? "แบบร่างรอบโอน" : formatDate(settlement.paidAt || settlement.createdAt)}</strong>
                          <span>{isDraft ? `แก้ล่าสุด ${formatDateTime(settlement.updatedAt || settlement.createdAt)}` : `ยอดจากระบบ ${money(settlement.systemDueAmount)}`}</span>
                        </div>
                        <div><span>โอนจริง</span><strong>{money(settlement.actualTransferAmount)}</strong></div>
                        <div><span>WHT เพิ่ม</span><strong>{money(settlement.payrollWhtAmount)}</strong></div>
                        <div><span>ประกันสังคม</span><strong>{money(settlement.socialSecurityAmount)}</strong></div>
                        <button
                          className="payment-mini-btn"
                          type="button"
                          onClick={() => isDraft ? openSalarySettlement(detailTeam) : openSettlementEdit(settlement)}
                        >
                          {isDraft ? "แก้ก่อนโอน" : "แก้ไข"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            <div className="payment-modal-footer">
              <div>
                <span>{detailTeam.dueCount ? `ถึงกำหนด ${fmt(detailTeam.dueCount)} รายการ` : detailTeam.scheduledCount ? `รอรอบ ${formatPayday(detailTeam.nextPayday)}` : "ไม่มีรายการค้าง"}</span>
                <strong>{money(detailTeam.dueCount ? detailTeam.dueAmount : detailTeam.scheduledAmount)}</strong>
              </div>
              <button
                className="payment-paid-btn"
                type="button"
                disabled={(!detailTeam.dueCount && !(detailTeam.salaried && detailTeam.scheduledCount > 0)) || savingTeam === detailTeam.team}
                onClick={() => requestMarkPaid(detailTeam)}
              >
                {detailTeam.salaried && (detailTeam.dueCount || detailTeam.scheduledCount)
                  ? (getSalaryDraftForGroup(detailTeam)
                      ? (detailTeam.dueCount ? "แก้รอบโอน / ยืนยัน" : "แก้ร่างรอบโอน")
                      : (detailTeam.dueCount ? "เตรียมรอบโอน" : "เตรียมยอดก่อนโอน"))
                  : detailTeam.dueCount
                    ? "โอนรายการที่ถึงกำหนด"
                    : "โอนครบแล้ว"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmTeam && (
        <div className="payment-modal-overlay" onMouseDown={() => !savingTeam && setConfirmTeam(null)}>
          <div className="payment-modal payment-confirm-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="payment-confirm-icon">✓</div>
            <h3>ยืนยันว่าโอนแล้ว?</h3>
            <p>ทีม <strong>{formatTeamWithRealName(confirmTeam.team, realNameByTeam)}</strong> · {fmt(confirmTeam.dueCount)} รายการ</p>
            <div className="payment-confirm-amount">{money(confirmTeam.dueAmount)}</div>
            {confirmTeam.dueExtraNet > 0 && (
              <div className="payment-confirm-breakdown">ค่ากดสุทธิ {money(confirmTeam.duePressAmount)} + รายได้พิเศษสุทธิ {money(confirmTeam.dueExtraNet)} (บริษัทออก WHT แยก {money(confirmTeam.dueExtraWht)})</div>
            )}
            <div className="payment-confirm-scope">ระบบจะเปลี่ยนสถานะเฉพาะรายการที่ตรงกับตัวกรองปัจจุบันและถึงกำหนดโอนแล้ว ({dateScopeLabel})</div>
            <div className="payment-confirm-actions">
              <button type="button" className="payment-cancel-btn" disabled={!!savingTeam} onClick={() => setConfirmTeam(null)}>ยกเลิก</button>
              <button type="button" className="payment-paid-btn" disabled={!!savingTeam} onClick={handleMarkPaidStandard}>{savingTeam ? "กำลังบันทึก…" : "ยืนยันว่าโอนแล้ว"}</button>
            </div>
          </div>
        </div>
      )}

      {settlementTeam && (
        <div className="payment-modal-overlay" onMouseDown={() => !savingTeam && setSettlementTeam(null)}>
          <div className="payment-modal payment-settlement-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="payment-modal-head">
              <div>
                <span className="payment-modal-kicker">SALARY SETTLEMENT</span>
                <h3>{salaryDraft ? "แก้ยอดที่เตรียมไว้" : "เตรียมยอดก่อนโอน"} · {settlementTeam.team}</h3>
                <p>{settlementTeam.realName ? `ชื่อจริง: ${settlementTeam.realName}` : "⚠ ยังไม่ได้กรอกชื่อจริง"}</p>
              </div>
              <button className="payment-modal-close" type="button" onClick={() => setSettlementTeam(null)}>×</button>
            </div>

            <div className={`payment-settlement-timing ${settlementTeam.salaryCanConfirm ? "is-due" : "is-preparing"}`}>
              <div>
                <strong>{settlementTeam.salaryCanConfirm ? "ถึงรอบโอนแล้ว" : "กำลังเตรียมยอดล่วงหน้า"}</strong>
                <span>รอบ {formatPayday(settlementTeam.salaryRoundDate)} · แก้ตัวเลขและบันทึกแบบร่างได้ตลอดก่อนโอนจริง</span>
              </div>
              <span>{settlementTeam.salaryCanConfirm ? "ยืนยันโอนได้" : "ยังไม่เปลี่ยนสถานะเป็นโอนแล้ว"}</span>
            </div>

            <div className="payment-settlement-system-summary">
              <div><span>ค่ากดสุทธิถึงรอบ</span><strong>{money(settlementTeam.duePressAmount)}</strong></div>
              <div><span>รายได้พิเศษที่ทีมได้รับ</span><strong>{money(settlementTeam.dueExtraNet)}</strong><small>บริษัทออก WHT เพิ่ม {money(settlementTeam.dueExtraWht)}</small></div>
              {(() => {
                const result = calcCompanyTopUp({
                  systemDueAmount: settlementTeam.dueAmount,
                  actualTransferAmount: settlementForm.actualTransfer === "" ? salarySuggestedTransfer : settlementForm.actualTransfer,
                  payrollWhtAmount: payrollWhtValue,
                  socialSecurityAmount: socialSecurityValue,
                });
                if (result.topUp > 0) {
                  return <div><span>บริษัทจ่ายเพิ่ม (ค่าใช้จ่ายบริษัท)</span><strong>+ {money(result.topUp)}</strong><small>โอนเกินจากที่ทำได้ · ค่าจ้างรวม {money(result.paidWage)}{Number(settlementTeam.salarySupportAmount || 0) > 0 ? ` · เงินเดือนขั้นต่ำ ${money(getSalaryFloor(settlementTeam.team))}` : ""}</small></div>;
                }
                if (result.shortfall > 0) {
                  return <div><span>⚠ จ่ายขาดจากที่ทำได้</span><strong>− {money(result.shortfall)}</strong><small>ยอดโอน + WHT + ประกันสังคม น้อยกว่ายอดที่ทีมทำได้</small></div>;
                }
                return null;
              })()}
              <div className="is-total"><span>ยอดจากระบบ (ค่ากด + รายได้พิเศษ)</span><strong>{money(settlementTeam.dueAmount)}</strong><small>ค่ากด {fmt(settlementTeam.duePressList?.length)} รายการ / {fmt(settlementTeam.dueQuantity)} ใบ · รายได้พิเศษ {fmt(settlementTeam.dueExtraList?.length)} รายการ</small></div>
              <div><span>เหลือค้างหลังรอบนี้</span><strong>{money(settlementTeam.remainingAfterRound?.total)}</strong><small>{settlementTeam.remainingAfterRound?.count ? `${fmt(settlementTeam.remainingAfterRound.count)} รายการ · รอบ ${formatPayday(settlementTeam.remainingAfterRound.nextDate)}` : "ไม่มี"}</small></div>
            </div>

            <div className="payment-form-grid">
              <label>
                <span>หัก ณ ที่จ่ายเพิ่มเติมของรอบเงินเดือน</span>
                <div className="payment-money-input"><input type="number" min="0" step="0.01" value={settlementForm.payrollWht} onChange={(e) => setSettlementForm((p) => ({ ...p, payrollWht: e.target.value }))} /><b>บาท</b></div>
                <small>ใช้สำหรับ WHT ที่ทราบภายหลัง ไม่แก้ WHT ของรายการค่ากดย้อนหลัง</small>
              </label>
              <label>
                <span>หักประกันสังคม</span>
                <div className="payment-money-input"><input type="number" min="0" step="0.01" value={settlementForm.socialSecurity} onChange={(e) => setSettlementForm((p) => ({ ...p, socialSecurity: e.target.value }))} /><b>บาท</b></div>
              </label>
              <label className="payment-form-span-2">
                <span>ยอดโอนจริง</span>
                <div className="payment-money-input payment-money-input--strong"><input type="number" min="0" step="0.01" value={settlementForm.actualTransfer} placeholder={String(salarySuggestedTransfer)} onChange={(e) => setSettlementForm((p) => ({ ...p, actualTransfer: e.target.value }))} /><b>บาท</b></div>
                <small>ถ้าเว้นว่าง ระบบใช้ {money(salarySuggestedTransfer)} · สามารถกรอกยอดจริงเองได้ถ้ารอบนี้มีเงินเดือนหรือรายการอื่นรวมอยู่ด้วย</small>
              </label>
              <label className="payment-form-span-2">
                <span>หมายเหตุรอบโอน</span>
                <textarea rows="3" value={settlementForm.note} onChange={(e) => setSettlementForm((p) => ({ ...p, note: e.target.value }))} placeholder="เช่น รวมเงินเดือน / ปรับยอดย้อนหลัง" />
              </label>
            </div>

            <div className="payment-modal-footer">
              <div>
                <span>{salaryDraft ? "แบบร่างที่บันทึกไว้ · ยอดที่จะโอน" : "ยอดที่จะโอนตามที่เตรียมไว้"}</span>
                <strong>{money(settlementForm.actualTransfer === "" ? salarySuggestedTransfer : settlementForm.actualTransfer)}</strong>
              </div>
              <div className="payment-confirm-actions">
                <button className="payment-cancel-btn" type="button" disabled={!!savingTeam} onClick={handleSaveSalaryDraft}>
                  {savingTeam ? "กำลังบันทึก…" : "บันทึกแบบร่าง"}
                </button>
                <button className="payment-paid-btn" type="button" disabled={!!savingTeam || !settlementTeam.salaryCanConfirm} onClick={handleConfirmSalaryPaid}>
                  {savingTeam ? "กำลังบันทึก…" : settlementTeam.salaryCanConfirm ? "ยืนยันว่าโอนแล้ว" : `ยืนยันได้ ${formatPayday(settlementTeam.salaryRoundDate)}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {extraModalOpen && (
        <div className="payment-modal-overlay" onMouseDown={() => !savingExtra && setExtraModalOpen(false)}>
          <div className="payment-modal payment-extra-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="payment-modal-head">
              <div>
                <span className="payment-modal-kicker">EXTRA INCOME</span>
                <h3>{editingExtra ? "แก้ไขรายได้พิเศษ" : "เพิ่มรายได้ให้ทีม"}</h3>
                <p>ยอดที่กรอกคือเงินที่ทีมต้องได้รับเต็ม · บริษัทออกหัก ณ ที่จ่าย 3% เพิ่มต่างหาก</p>
              </div>
              <button className="payment-modal-close" type="button" onClick={() => setExtraModalOpen(false)}>×</button>
            </div>

            <div className="payment-form-grid">
              <label>
                <span>ทีม</span>
                <select value={extraForm.teamName} disabled={!!editingExtra} onChange={(e) => setExtraForm((p) => ({ ...p, teamName: e.target.value }))}>
                  <option value="">เลือกทีม</option>
                  {TEAM_MEMBERS.filter((name) => name !== OWNER_TEAM).map((name) => (
                    <option key={name} value={name}>{formatTeamWithRealName(name, realNameByTeam)}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>ประเภท</span>
                <select value={extraForm.category} onChange={(e) => setExtraForm((p) => ({ ...p, category: e.target.value }))}>
                  <option value="ค่าคอม">ค่าคอม</option>
                  <option value="ช่วยแอดมิน">ค่าช่วยแอดมิน</option>
                  <option value="อื่นๆ">อื่นๆ</option>
                </select>
              </label>
              <label>
                <span>วันที่</span>
                <input type="date" value={extraForm.date} onChange={(e) => setExtraForm((p) => ({ ...p, date: e.target.value }))} />
              </label>
              <label>
                <span>ทีมต้องได้รับ</span>
                <div className="payment-money-input"><input type="number" min="0" step="0.01" value={extraForm.amount} onChange={(e) => setExtraForm((p) => ({ ...p, amount: e.target.value }))} /><b>บาท</b></div>
              </label>
              <label className="payment-form-span-2">
                <span>รายละเอียด</span>
                <input value={extraForm.description} onChange={(e) => setExtraForm((p) => ({ ...p, description: e.target.value }))} placeholder="เช่น ช่วยตอบลูกค้า / ช่วยเช็กข้อมูล" />
              </label>
              <div className="payment-form-span-2 payment-extra-status-field">
                <span>สถานะการโอน</span>
                <div className="payment-extra-status-options">
                  <button
                    type="button"
                    className={extraForm.paymentStatus === "unpaid" ? "is-active" : ""}
                    onClick={() => setExtraForm((p) => ({ ...p, paymentStatus: "unpaid" }))}
                  >
                    ยังไม่โอน
                  </button>
                  <button
                    type="button"
                    className={extraForm.paymentStatus === "paid" ? "is-active is-paid" : ""}
                    onClick={() => setExtraForm((p) => ({ ...p, paymentStatus: "paid" }))}
                  >
                    โอนไปแล้ว
                  </button>
                </div>
                <small>“ยังไม่โอน” จะรวมในยอดรอโอน · “โอนไปแล้ว” จะเก็บไว้ในประวัติและไม่เพิ่มยอดรอโอน</small>
              </div>
            </div>

            <div className="payment-extra-preview">
              <div className="is-net"><span>ทีมได้รับ</span><strong>{money(extraPreviewNet)}</strong></div>
              <div><span>WHT 3% บริษัทออกแทนตลอดไป</span><strong>+ {money(extraPreviewWht)}</strong><small>เงินได้ตาม 50 ทวิ {money(extraPreview.whtBase)}</small></div>
              <div><span>ต้นทุนบริษัทรวม</span><strong>{money(extraPreviewGross)}</strong></div>
            </div>

            <div className="payment-modal-footer">
              <div>
                <span>{extraForm.paymentStatus === "paid" ? "สถานะ" : "รอบโอน"}</span>
                <strong>{extraForm.paymentStatus === "paid" ? "โอนไปแล้ว · เก็บในประวัติ" : (extraForm.teamName && isSalariedTeam(extraForm.teamName) ? formatPayday(getSalaryPayday(dateInputToBangkokDate(extraForm.date))) : "รอบโอนปกติ")}</strong>
              </div>
              <button className="payment-paid-btn" type="button" disabled={savingExtra} onClick={handleSaveExtraIncome}>{savingExtra ? "กำลังบันทึก…" : editingExtra ? "บันทึกการแก้ไข" : extraForm.paymentStatus === "paid" ? "บันทึกเป็นโอนไปแล้ว" : "เพิ่มรายได้ทีม"}</button>
            </div>
          </div>
        </div>
      )}

      {ownerModalOpen && (
        <div className="payment-modal-overlay" onMouseDown={() => setOwnerModalOpen(false)}>
          <div className="payment-modal payment-detail-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="payment-modal-head">
              <div>
                <span className="payment-modal-kicker">OWNER PROFIT</span>
                <h3>ค่ากดของยย</h3>
                <p>ไม่ต้องโอน · แก้เฉพาะค่ากดที่กรอกผิดได้</p>
              </div>
              <button className="payment-modal-close" type="button" onClick={() => setOwnerModalOpen(false)}>×</button>
            </div>
            <div className="payment-owner-modal-total"><span>รวมตามช่วงที่เลือก</span><strong>{money(ownerTotal)}</strong></div>
            <div className="payment-record-list">
              {ownerRecords.map((record) => {
                const fee = calculateRecordFee(record);
                return (
                  <div className="payment-record-row" key={record.ref?.path || record.id}>
                    <div className="payment-record-copy">
                      <div className="payment-record-title"><strong>{record.eventName || "ไม่ระบุชื่องาน"}</strong></div>
                      <div className="payment-record-meta"><span>{record.zone || "ไม่ระบุโซน"}</span><span>•</span><span>{fmt(record.quantity)} ใบ</span><span>•</span><span>{formatDateTime(record.createdAt)}</span><span>•</span><span>{money(fee.feePer)}/ใบ</span></div>
                    </div>
                    <div className="payment-record-side-actions">
                      <strong className="payment-record-amount">{money(fee.baseFee)}</strong>
                      <button className="payment-mini-btn" type="button" onClick={() => openOwnerEdit(record)}>แก้ค่ากด</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {ownerEditRecord && (
        <div className="payment-modal-overlay payment-modal-overlay--top" onMouseDown={() => !savingOwnerFee && setOwnerEditRecord(null)}>
          <div className="payment-modal payment-small-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="payment-modal-head">
              <div><span className="payment-modal-kicker">EDIT PRESS FEE</span><h3>แก้ / ลบค่ากดของยย</h3><p>{ownerEditRecord.eventName} · {ownerEditRecord.zone}</p></div>
              <button className="payment-modal-close" type="button" onClick={() => setOwnerEditRecord(null)}>×</button>
            </div>
            <div className="payment-single-form">
              <label><span>ค่ากดต่อใบ</span><div className="payment-money-input"><input type="number" min="0" step="0.01" value={ownerFeeInput} onChange={(e) => setOwnerFeeInput(e.target.value)} /><b>บาท</b></div></label>
              <label><span>จำนวนใบ</span><div className="payment-money-input"><input type="number" min="1" step="1" value={ownerQtyInput} onChange={(e) => setOwnerQtyInput(e.target.value)} /><b>ใบ</b></div></label>
              <div className="payment-edit-preview"><span>{fmt(Number(ownerQtyInput || 0))} ใบ</span><strong>รวม {money(roundMoney(Number(ownerFeeInput || 0) * Number(ownerQtyInput || 0)))}</strong></div>
              <div className={`payment-owner-delete ${ownerDeleteConfirm ? "is-confirm" : ""}`}>
                <span>{ownerDeleteConfirm ? "ยืนยันลบรายการนี้? กำไรบริษัทจะลดลงตามยอดรายการนี้ · ข้อมูลเดิมเก็บไว้ใน Audit Log" : "ไม่ได้กดงานนี้จริง / กรอกซ้ำ?"}</span>
                <button type="button" className="payment-cancel-btn" disabled={savingOwnerFee} onClick={handleDeleteOwnerRecord}>{ownerDeleteConfirm ? "ยืนยันลบ" : "ลบรายการนี้"}</button>
                {ownerDeleteConfirm && <button type="button" className="payment-mini-btn" disabled={savingOwnerFee} onClick={() => setOwnerDeleteConfirm(false)}>ไม่ลบ</button>}
              </div>
            </div>
            <div className="payment-modal-footer"><div><span>รายการเดิมจะถูกแก้ทับ</span><strong>ข้อมูลรายงานจะอัปเดตตาม</strong></div><button className="payment-paid-btn" type="button" disabled={savingOwnerFee} onClick={handleSaveOwnerFee}>{savingOwnerFee ? "กำลังบันทึก…" : "บันทึกการแก้ไข"}</button></div>
          </div>
        </div>
      )}

      {editingSettlement && (
        <div className="payment-modal-overlay payment-modal-overlay--top" onMouseDown={() => !savingSettlement && setEditingSettlement(null)}>
          <div className="payment-modal payment-small-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="payment-modal-head">
              <div><span className="payment-modal-kicker">EDIT SETTLEMENT</span><h3>แก้ข้อมูลรอบโอน</h3><p>{formatTeamWithRealName(editingSettlement.teamName, realNameByTeam)} · {formatDate(editingSettlement.paidAt || editingSettlement.createdAt)}</p></div>
              <button className="payment-modal-close" type="button" onClick={() => setEditingSettlement(null)}>×</button>
            </div>
            <div className="payment-form-grid payment-form-grid--single">
              {(() => {
                const result = calcCompanyTopUp({
                  systemDueAmount: editingSettlement.systemDueAmount,
                  actualTransferAmount: settlementEditForm.actualTransfer,
                  payrollWhtAmount: settlementEditForm.payrollWht,
                  socialSecurityAmount: settlementEditForm.socialSecurity,
                });
                return (
                  <div className={`payment-topup-note ${result.shortfall ? "is-short" : ""}`}>
                    ยอดที่ทีมทำได้ {money(editingSettlement.systemDueAmount)} · ค่าจ้างที่จ่ายจริง (โอน + WHT + ประกันสังคม) {money(result.paidWage)}
                    {result.topUp > 0 && <strong> · บริษัทจ่ายเพิ่ม {money(result.topUp)} (ค่าใช้จ่ายบริษัท)</strong>}
                    {result.shortfall > 0 && <strong> · ⚠ จ่ายขาด {money(result.shortfall)}</strong>}
                  </div>
                );
              })()}
              <label><span>ยอดโอนจริง</span><div className="payment-money-input"><input type="number" min="0" step="0.01" value={settlementEditForm.actualTransfer} onChange={(e) => setSettlementEditForm((p) => ({ ...p, actualTransfer: e.target.value }))} /><b>บาท</b></div></label>
              <label><span>WHT เพิ่มเติม</span><div className="payment-money-input"><input type="number" min="0" step="0.01" value={settlementEditForm.payrollWht} onChange={(e) => setSettlementEditForm((p) => ({ ...p, payrollWht: e.target.value }))} /><b>บาท</b></div></label>
              <label><span>ประกันสังคม</span><div className="payment-money-input"><input type="number" min="0" step="0.01" value={settlementEditForm.socialSecurity} onChange={(e) => setSettlementEditForm((p) => ({ ...p, socialSecurity: e.target.value }))} /><b>บาท</b></div></label>
              <label><span>หมายเหตุ</span><textarea rows="3" value={settlementEditForm.note} onChange={(e) => setSettlementEditForm((p) => ({ ...p, note: e.target.value }))} /></label>
            </div>
            <div className="payment-modal-footer"><div><span>ยอดจากระบบเดิม</span><strong>{money(editingSettlement.systemDueAmount)}</strong></div><button className="payment-paid-btn" type="button" disabled={savingSettlement} onClick={handleUpdateSettlement}>{savingSettlement ? "กำลังบันทึก…" : "บันทึกการแก้ไข"}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
